import type { GamesByBrand } from '@/api/worldCup';
import { ChartCard } from '../ui/ChartCard';
import styles from '../SegmentTable/style.module.css';

type Props = {
  rows: GamesByBrand[];
  brandColors: Record<string, string>;
  /** Per-brand active-weeks string (e.g. "W22-W25") looked up by brand_id. */
  brandWeeks?: Record<string, string>;
};

// Parse the first ISO week number out of a "W22-W25" / "W22, W23" / "W27" string.
// Returns Infinity for missing / unparseable values so they sink to the bottom.
function startWeek(weeks: string | undefined): number {
  if (!weeks) return Infinity;
  const m = /W(\d+)/.exec(weeks);
  return m ? Number(m[1]) : Infinity;
}

export function GamesByBrandTable({ rows, brandColors, brandWeeks = {} }: Props) {
  // Stable sort by campaign start week (ascending). Brands that share a start
  // week stay in the order the backend shipped them.
  const sorted = [...rows].sort((a, b) => startWeek(brandWeeks[a.brand_id]) - startWeek(brandWeeks[b.brand_id]));

  return (
    <ChartCard title="Games details by brand">
      <table className={styles.table}>
        <colgroup>
          <col style={{ width: '34%' }} />
          <col style={{ width: '18%' }} />
          <col style={{ width: '22%' }} />
          <col style={{ width: '26%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>Brand</th>
            <th>Weeks</th>
            <th className={styles.num}>Plays</th>
            <th className={styles.num}>Avg / user</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={r.brand_id}>
              <td>
                <span className={styles.nameCell}>
                  <span className={styles.dot} style={{ background: brandColors[r.brand_id] ?? 'var(--ink)' }} />
                  <span className={styles.brandName}>{r.brand}</span>
                </span>
              </td>
              <td>
                {brandWeeks[r.brand_id] ? (
                  <span className={styles.week}>{brandWeeks[r.brand_id]}</span>
                ) : (
                  <span className="muted" style={{ fontSize: 11 }}>
                    —
                  </span>
                )}
              </td>
              <td className={`${styles.num} ${styles.value}`}>{r.plays.display}</td>
              <td className={`${styles.num} mono`}>{r.avg_per_user.toFixed(1)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ChartCard>
  );
}
